import lodash from 'lodash';
import moment from 'moment';
import { BeneficiaryTypeEnum, CustomerInfoKeyType, CustomerInfoType } from 'types/customer';
import { AccountItemType, ApiWebUserData, WebUserAccountTypeEnum } from 'types/user';

// 有在项目中用到的, 表单值是字符串的, 但从接口拿到的是数字的, 都可以在此处做类型转换
// 这里仅允许定义老字段, 新字段一律与server类型保持统一,不做转换
export const toStringArr: CustomerInfoKeyType[] = [
    'same_delivery_and_residence',
    'regulatory_relationship',
    'net_assets',
    'net_current_assets',
    'net_year_income',
    'stk_kl'
];

export function fixDataToLocal(serverData: CustomerInfoType): CustomerInfoType {
    const info = lodash.cloneDeep(serverData);
    const { beneficiary_info } = info;

    /* --------------- 公共信息处理 --------------- */

    // 将部分字段的值转换为字符串类型, 接口返回值中绝大多数值都是number类型, 项目的表单中用到的大多数都是string, 所以需要在这里做一个转化
    toStringArr.forEach(name => {
        const curValue = info[name];

        if (typeof curValue === 'boolean') {
            (info[name] as any) = String(Number(curValue));
        } else if (typeof curValue === 'number') {
            (info[name] as any) = String(curValue);
        }
    });

    // 将所有值为null的字段删除
    lodash.each(info, (value, key) => {
        if (value === null) {
            delete info[key];
        }

        // 所有的权限类型全部转为字符串
        if (key.endsWith('_perm')) {
            info[key] = String(info[key]);
        }
    });

    /* --------------- 基础信息模块 --------------- */
    if (info.mask_id_no) {
        info.id_no = info.mask_id_no;
    }

    if (info.birthday) {
        info.age = moment().diff(info.birthday, 'years');
    }

    /* --------------- 工作/规管信息模块 --------------- */
    // temporary_regulatory_members_checked是前端临时字段
    // temporary_regulatory_members_checked的值为1时，regulatory_relationship才会有值
    // 所以这里需要反向计算一下
    if (info.regulatory_relationship) {
        info.temporary_regulatory_members_checked = '1';
    }

    if (info.regulatory_company_other) {
        info.temporary_employee_track_checked = '1';
    }

    if (info.regulatory_stockholder) {
        info.temporary_regulatory_stockholder_checked = '1';
    }

    /* --------------- 受益人信息 --------------- */
    if (beneficiary_info) {
        // 历史beneficiary_info收集证件号，现在是收集纳税国和税号，且server侧已全量清洗数据。
        const curBeneficiaryInfo = beneficiary_info.map(item => {
            if (item.mask_tax_identification_number) {
                item.tax_identification_number = item.mask_tax_identification_number;
            }

            return item;
        });

        info.temporaryPrimaryBeneficiary = curBeneficiaryInfo.filter(
            i => i.beneficiary_type === BeneficiaryTypeEnum.PRIMARY
        );

        info.temporarySecondaryBeneficiary = curBeneficiaryInfo.filter(
            i => i.beneficiary_type === BeneficiaryTypeEnum.SECONDARY
        );
    }

    return info;
}

/**
 * 一些字段server接受的类型是Number(常见于FormSelect组件上), 但是前端侧因为:
 * 1. 历史原因: server原来使用的python, 兼容了前端传string和number的情况, 但是更换成go之后,严格要求类型
 * 2. 前端表单自身原因: 表单的值默认都是string的, 有些可能漏处理了parser和formatter
 * 3. 表单自身原因(空值问题): 即便在FormGroup上使用了$parser和$formatter做了转化, 但是用户没有选择且该项表单为可选且没有设置默认值的情况下, 表单的值是空字符串, server不接受字符串
 * server使用ajv库做数据校验, 空字符串无法转化, 所以针对这部分字段, 做一个兜底处理, 要在提交给server的时候, 如果是空字符串, 要删除该字段, 如果不是空字符串, 就转换成数字
 */
const numberFieldsToServer: CustomerInfoKeyType[] = ['suffix'];

/**
 * @description 存在部分数据, 前端跟server字段名不一样, 需要做一下转换
 * 同时有部分数据, 仅前端需要, 不需要传给后端
 */
export function fixDataToServer(localData: Partial<CustomerInfoType>) {
    const cloneCustomerInfo = lodash.cloneDeep(localData);
    const { mask_id_no, id_no, temporaryPrimaryBeneficiary, temporarySecondaryBeneficiary } = cloneCustomerInfo;

    /* --------------- 基础信息 --------------- */
    // 对证件号做的脱敏处理
    // server不接收打了掩码的id_no
    if (mask_id_no && id_no && id_no === mask_id_no) {
        delete cloneCustomerInfo.id_no;
    }

    /* --------------- 受益人信息 --------------- */
    // 需要把主受益人和次受益人合并成一个
    if (temporaryPrimaryBeneficiary) {
        cloneCustomerInfo.beneficiary_info = temporaryPrimaryBeneficiary
            .concat(temporarySecondaryBeneficiary || [])
            .map(i => {
                // 如果税号是打了掩码的, 就设置为空, server不接受打了掩码的税号
                if (
                    i.mask_tax_identification_number &&
                    i.tax_identification_number &&
                    i.tax_identification_number === i.mask_tax_identification_number
                ) {
                    i.tax_identification_number = '';
                }

                return i;
            });
    }

    /* --------------- 基础/统一处理 --------------- */
    // 页面中会存在一些临时存储数据的表单,都以temporary开头
    // 这些数据不需要传给server, 在这里统一处理删除掉
    Object.keys(cloneCustomerInfo).forEach(item => {
        if (item.startsWith('temporary')) {
            delete cloneCustomerInfo[item];
        }
    });

    numberFieldsToServer.forEach(field => {
        const curValue = cloneCustomerInfo[field];

        if (curValue === '') {
            delete cloneCustomerInfo[field];
        } else {
            cloneCustomerInfo[field] = Number(curValue) as any;
        }
    });

    return cloneCustomerInfo;
}

/**
 * @description 选择当前的账户: 只有url上没有customerId的情况下才需要使用此方法来判断当前账户
 *  如果有currentCustomerId, 就使用对应账号;
 *  否则使用accountList的第一个账号作为当前账号
 * @param {ApiWebUserData} apiUserData /web/user/status接口获取到的用户信息
 * @return {AccountItemType} 当前账户信息
 */
export function chooseCurrentAccount({
    actualAccountInfo: { accountList, currentCustomerId }
}: ApiWebUserData): AccountItemType {
    // 开户中不支持虚拟账户, 需要过滤掉
    const curAccountList = accountList.filter(i => i.accountType !== WebUserAccountTypeEnum.VIRTUAL);

    // currentCustomerId存在 且 能在accountList找到对应的账户, 就用该账户
    // 否则使用accountList的第一个账户
    return (
        (currentCustomerId && curAccountList.find(account => account.customerId === currentCustomerId)) ||
        // 最后的默认值, 是为了避免server临时更换数据, accountList是空数组做的兼容
        curAccountList[0] || { status: 'UNACCOUNT' }
    );
}
